import React, { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Configure axios defaults
const getApiBaseUrl = () => {
  // In production, use relative URL so it works with current domain
  if (import.meta.env.PROD) {
    return '/api'
  }
  // In development, force backend port to ensure correct routing
  return 'http://localhost:5000/api'
}

axios.defaults.baseURL = getApiBaseUrl()
axios.defaults.withCredentials = true

// The access token is held in memory only — never localStorage/sessionStorage —
// so a successful XSS cannot read or exfiltrate a long-lived credential. The
// session is anchored by the httpOnly refresh cookie (set by the server), which
// survives page reloads; on app load we silently exchange it for a fresh access
// token (see initAuth). Only the non-sensitive "stay signed in" preference is
// persisted to localStorage.
let accessToken = null
export const setAccessToken = (t) => { accessToken = t || null }
export const getAccessToken = () => accessToken

// Request interceptor to add auth token
axios.interceptors.request.use(
  (config) => {
    // Skip adding token for login and refresh requests
    const isLoginRequest = config.url?.includes('/auth/login') || config.url?.endsWith('/login')
    const isRefreshRequest = config.url?.includes('/auth/refresh') || config.url?.endsWith('/refresh')

    if (!isLoginRequest && !isRefreshRequest && accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config

    // Skip token refresh for login requests to prevent cascading errors
    const isLoginRequest = original.url?.includes('/auth/login') || original.url?.endsWith('/login')
    const isRefreshRequest = original.url?.includes('/auth/refresh') || original.url?.endsWith('/refresh')
    
    if (error.response?.status === 401 && !original._retry && !isLoginRequest && !isRefreshRequest) {
      original._retry = true

      try {
        // Check if user opted for stay signed in
        const staySignedIn = localStorage.getItem('staySignedIn') === 'true'
        console.log('Attempting token refresh for staySignedIn:', staySignedIn)
        
        const response = await axios.post('/auth/refresh', { staySignedIn })
        const { token } = response.data

        // Hold the refreshed token in memory only.
        setAccessToken(token)

        // Update the original request with the new token
        original.headers.Authorization = `Bearer ${token}`
        return axios(original)
      } catch (refreshError) {
        console.log('Token refresh failed:', refreshError.response?.data || refreshError.message)

        // Clear the in-memory token and the stay-signed-in preference
        setAccessToken(null)
        localStorage.removeItem('staySignedIn')
        
        // Show a user-friendly message only if not on login page
        if (!window.location.pathname.includes('/login')) {
          toast.error('Your session has expired. Please log in again.')
        }
        
        // Force redirect immediately - don't wait, but only if not already on login page
        if (!window.location.pathname.includes('/login')) {
          console.log('Redirecting to login page')
          window.location.href = '/login'
        }
        
        return Promise.reject(refreshError)
      }
    }

    // Handle other types of 401 errors (like invalid tokens on first request)
    if (error.response?.status === 401 && original._retry && !isLoginRequest) {
      console.log('Authentication failed after token refresh attempt')

      // Clear the in-memory token
      setAccessToken(null)
      localStorage.removeItem('staySignedIn')
      
      // Show error only if not on login page
      if (!window.location.pathname.includes('/login')) {
        toast.error('Authentication failed. Please log in again.')
        
        setTimeout(() => {
          window.location.href = '/login'
        }, 1000)
      }
    }

    return Promise.reject(error)
  }
)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const initAuth = async () => {
      // The in-memory access token doesn't survive a reload, so exchange the
      // httpOnly refresh cookie for a fresh one. A 401 here just means there's no
      // valid session (guest) — stay logged out silently.
      try {
        const staySignedIn = localStorage.getItem('staySignedIn') === 'true'
        const { data } = await axios.post('/auth/refresh', { staySignedIn })
        if (data?.token) {
          setAccessToken(data.token)
          const verify = await axios.get('/auth/verify')
          setUser(verify.data.user)
        }
      } catch (error) {
        setAccessToken(null)
      }
      setLoading(false)
    }

    initAuth()
  }, [])

  // Separate effect for periodic token validation to avoid infinite loops
  useEffect(() => {
    if (user) {
      const interval = setInterval(async () => {
        if (getAccessToken()) {
          try {
            await axios.get('/auth/verify')
          } catch (error) {
            // The response interceptor will handle the token refresh/logout
            console.log('Background token validation failed, response interceptor will handle it')
          }
        }
      }, 30 * 60 * 1000) // Check every 30 minutes (less frequent since tokens last longer)

      return () => clearInterval(interval)
    }
  }, [user?.id]) // Only depend on user ID to avoid constant re-runs

  const login = async (email, password, staySignedIn = false, totpToken = null) => {
    try {
      const response = await axios.post('/auth/login', {
        email,
        password,
        staySignedIn,
        ...(totpToken ? { totpToken } : {}),
      })

      // Server asks for 2FA code before issuing token
      if (response.data.requires2FA) {
        return { success: false, requires2FA: true }
      }

      // Server requires MFA setup before completing login
      if (response.data.requiresMfaSetup) {
        return { success: false, requiresMfaSetup: true, setupToken: response.data.setupToken }
      }

      const { token, user } = response.data

      // Token lives in memory only; persist just the (non-sensitive) preference
      // so a reload can request the right refresh-cookie lifetime.
      if (staySignedIn) {
        localStorage.setItem('staySignedIn', 'true')
      } else {
        localStorage.removeItem('staySignedIn')
      }
      setAccessToken(token)

      setUser(user)

      toast.success(`Welcome back, ${user.name}!`)
      return { success: true, user }
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed'
      return { success: false, error: message }
    }
  }

  const logout = async () => {
    try {
      await axios.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setAccessToken(null)
      localStorage.removeItem('staySignedIn')
      setUser(null)
      toast.success('Logged out successfully')
    }
  }

  const register = async (userData) => {
    try {
      const response = await axios.post('/auth/register', userData)
      toast.success('User registered successfully')
      return { success: true, data: response.data }
    } catch (error) {
      const message = error.response?.data?.error || 'Registration failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/users/profile', profileData)
      setUser(prev => ({ ...prev, ...profileData }))
      toast.success('Profile updated successfully')
      return { success: true, data: response.data }
    } catch (error) {
      const message = error.response?.data?.error || 'Profile update failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await axios.put('/auth/password', {
        currentPassword,
        newPassword
      })
      toast.success('Password changed successfully')
      return { success: true, data: response.data }
    } catch (error) {
      const message = error.response?.data?.error || 'Password change failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const refreshUser = async () => {
    try {
      const response = await axios.get('/auth/verify')
      setUser(response.data.user)
      return response.data.user
    } catch (error) {
      console.error('Failed to refresh user data:', error)
      return null
    }
  }

  // Complete login after forced MFA setup — called by Login.jsx after /2fa/verify returns loginComplete
  const completeForcedMfaLogin = (token, userData) => {
    setAccessToken(token)
    localStorage.removeItem('staySignedIn')
    setUser(userData)
    toast.success(`Welcome back, ${userData.name}!`)
  }

  // Establish a session after a successful password reset — the backend revokes
  // every other session and hands this device a fresh token (ResetPassword.jsx).
  const completePasswordReset = (token, userData) => {
    setAccessToken(token)
    localStorage.removeItem('staySignedIn')
    setUser(userData)
  }

  // Establish a session after a Google sign-in that required a 2FA code. The
  // refresh cookie is already set by the server; we just adopt the access token
  // and load the full user (incl. clientData) via /verify. Used by Login.jsx.
  const completeGoogleLogin = async (token) => {
    setAccessToken(token)
    localStorage.removeItem('staySignedIn')
    try {
      const { data } = await axios.get('/auth/verify')
      setUser(data.user)
      toast.success(`Welcome back, ${data.user.name}!`)
      return data.user
    } catch {
      setAccessToken(null)
      return null
    }
  }

  const value = {
    user,
    loading,
    login,
    logout,
    register,
    updateProfile,
    changePassword,
    refreshUser,
    completeForcedMfaLogin,
    completePasswordReset,
    completeGoogleLogin,
    isAdmin: user?.role === 'admin',
    isClient: user?.role === 'client',
    isAuthenticated: !!user,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}